import React, { useState, useEffect } from 'react';
import { benchmarks as benchmarksApi } from '../../api/client';
import { Award, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

export default function BenchmarksPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await benchmarksApi.get();
        setData(result);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadData();
  }, []);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Industry</span>
        <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Benchmarks</h1>
        <p className="text-stone-500 mt-1">Category: {data.category}</p>
      </div>

      {/* Your Position */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Your Position</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="font-serif text-3xl text-stone-900 dark:text-white">{data.userRank?.percentile}th</div>
            <div className="text-xs text-stone-500">Percentile</div>
          </div>
          <div className="text-center">
            <div className="font-serif text-3xl text-stone-900 dark:text-white">#{data.userRank?.rankInCategory}</div>
            <div className="text-xs text-stone-500">Rank</div>
          </div>
          <div className="text-center">
            <div className="font-serif text-3xl text-stone-900 dark:text-white">{data.userRank?.totalInCategory}</div>
            <div className="text-xs text-stone-500">Total Brands</div>
          </div>
          <div className="text-center">
            <div className={`font-serif text-3xl ${data.userRank?.vsAverage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {data.userRank?.vsAverage >= 0 ? '+' : ''}{data.userRank?.vsAverage}
            </div>
            <div className="text-xs text-stone-500">vs Average</div>
          </div>
          <div className="text-center">
            <div className={`font-serif text-3xl ${data.userRank?.vsTopDecile >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {data.userRank?.vsTopDecile >= 0 ? '+' : ''}{data.userRank?.vsTopDecile}
            </div>
            <div className="text-xs text-stone-500">vs Top 10%</div>
          </div>
        </div>
      </div>

      {/* Category Benchmarks */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Category Stats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700">
            <div className="text-xs text-stone-500 mb-1">Average</div>
            <div className="font-serif text-2xl text-stone-900 dark:text-white">{data.benchmarks?.avgVisibility}</div>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700">
            <div className="text-xs text-stone-500 mb-1">Median</div>
            <div className="font-serif text-2xl text-stone-900 dark:text-white">{data.benchmarks?.medianVisibility}</div>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700">
            <div className="text-xs text-stone-500 mb-1">Top 10%</div>
            <div className="font-serif text-2xl text-emerald-600">{data.benchmarks?.topDecile}</div>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700">
            <div className="text-xs text-stone-500 mb-1">Bottom 10%</div>
            <div className="font-serif text-2xl text-rose-600">{data.benchmarks?.bottomDecile}</div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Industry Leaderboard</h2>
        <div className="space-y-2">
          {(data.leaderboard || []).map((entry) => (
            <div key={entry.rank} className="flex items-center gap-4 p-3 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700">
              <div className={`w-8 h-8 flex items-center justify-center font-medium text-sm ${
                entry.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'
              }`}>
                #{entry.rank}
              </div>
              <div className="flex-1">
                <span className="font-medium text-stone-900 dark:text-white">{entry.name}</span>
              </div>
              <div className="font-serif text-xl text-stone-900 dark:text-white">{entry.score}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
